import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Accessibility, Check } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {
  useA11yStore,
  type A11yTheme,
  type A11yFontScale,
  type A11yFontFamily,
} from '@/stores/a11yStore';

const THEMES: { value: A11yTheme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'high-contrast', label: 'High contrast' },
];

const SCALES: { value: A11yFontScale; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra large' },
];

const FAMILIES: { value: A11yFontFamily; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'dyslexic', label: 'Dyslexia-friendly' },
];

export interface A11yMenuProps {
  className?: string;
  /** Which side of the trigger button the panel should align to. Defaults to 'right'. */
  align?: 'left' | 'right';
}

/**
 * Accessibility preferences dropdown: theme, font scale, font family.
 * Persisted to localStorage via useA11yStore.
 */
export function A11yMenu({ className, align = 'right' }: A11yMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const { theme, fontScale, fontFamily, setTheme, setFontScale, setFontFamily, reset } =
    useA11yStore();

  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const width = 256; // w-64
      const top = rect.bottom + 8;
      const left = align === 'right' ? rect.right - width : rect.left;
      setMenuPos({ top, left: Math.max(8, Math.min(left, window.innerWidth - width - 8)) });
    };
    updatePos();
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open, align]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Accessibility preferences"
        title="Accessibility preferences"
        className={cn(
          'inline-flex items-center justify-center w-9 h-9 rounded-[var(--radius-md)]',
          'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
          'hover:bg-[var(--color-bg-tertiary)] transition-colors',
          open && 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-primary)]',
        )}
      >
        <Accessibility className="w-5 h-5" />
      </button>

      {open && menuPos && createPortal(
        <div
          role="menu"
          aria-label="Accessibility preferences"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
          className={cn(
            'w-64 z-[9999]',
            'bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
            'rounded-[var(--radius-md)] shadow-lg p-3 space-y-3',
          )}
          onMouseDown={(e) => {
            // Prevent outside-click handler (which uses mousedown on document) from closing.
            // The container's contains() check won't include this portal, so stop propagation.
            e.stopPropagation();
          }}
        >
          <Section label="Theme">
            {THEMES.map((opt) => (
              <OptionRow
                key={opt.value}
                active={theme === opt.value}
                onClick={() => setTheme(opt.value)}
                label={opt.label}
              />
            ))}
          </Section>
          <Section label="Text size">
            {SCALES.map((opt) => (
              <OptionRow
                key={opt.value}
                active={fontScale === opt.value}
                onClick={() => setFontScale(opt.value)}
                label={opt.label}
              />
            ))}
          </Section>
          <Section label="Font">
            {FAMILIES.map((opt) => (
              <OptionRow
                key={opt.value}
                active={fontFamily === opt.value}
                onClick={() => setFontFamily(opt.value)}
                label={opt.label}
              />
            ))}
          </Section>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              window.dispatchEvent(new Event('shopcommand:show-shortcuts'));
            }}
            className="w-full text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] py-1.5 border-t border-[var(--color-border)] text-left px-2"
          >
            Keyboard shortcuts…
          </button>
          <button
            type="button"
            onClick={reset}
            className="w-full text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] py-1.5 border-t border-[var(--color-border)]"
          >
            Reset to defaults
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide font-semibold text-[var(--color-text-secondary)] mb-1 px-1">
        {label}
      </p>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function OptionRow({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        'flex items-center justify-between px-2 py-1.5 rounded-[var(--radius-sm)] text-sm text-left',
        'hover:bg-[var(--color-bg-tertiary)] transition-colors',
        active && 'text-[var(--color-accent-primary)] font-medium',
      )}
    >
      <span>{label}</span>
      {active && <Check className="w-4 h-4" />}
    </button>
  );
}
